import { useRef, useState } from 'react';
import { Button } from '@/kit/Button';
import { NoteTagsTab } from '@/features/settings-b/NoteTagsTab';
import { InputField } from '@/kit/FormField';
import { RequiredMark } from '@/kit/RequiredMark';
import { Icon } from '@/kit/Icon';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { CAPIUM_TAG_GROUPS, appendCapiumTag } from '@/core/models/capium-tags';
import type { NoteEditorDialogData, NoteEditorDialogResult } from '@/core/models/report-settings.models';
import { notifyError } from '@/core/notify/notify';
import { validateForm } from './common';
import { standardNoteEditorConfig } from './noteEditorConfig';

const editorConfig = standardNoteEditorConfig();

/**
 * Angular PrinciplesActivitesDialogComponent: edits a report note's title and wording; the caller saves the returned
 * value. Also used by the Accounting Policy screen. The heading typo "Principles Activites" is verbatim.
 */
export function PrinciplesActivitesDialog({
  data,
  dialogRef,
}: DialogComponentProps<NoteEditorDialogData, NoteEditorDialogResult>) {
  const [title, setTitle] = useState(data?.title ?? '');
  const [content, setContent] = useState(data?.content ?? '');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const closing = useRef(false);

  /** ap5-a5: the merge tags the accounts substitute; clicking one appends it to the wording. */
  const insertTag = (tag: string) => setContent((current) => appendCapiumTag(current, tag));

  /**
   * AP parity: the 3.0 note popup's "Reset Default" link puts the catalogue title and wording back into the editor;
   * nothing is saved until Save, so Cancel still keeps the client's own wording.
   */
  const hasDefault = data?.defaultContent != null || data?.defaultTitle != null;
  const resetDefault = () => {
    if (data?.defaultTitle != null) setTitle(data.defaultTitle);
    if (data?.defaultContent != null) setContent(data.defaultContent);
  };

  const onSave = () => {
    if (closing.current) return;
    const trimmed = (title ?? '').trim();
    if (!trimmed) {
      setSubmitted(true);
      validateForm(false);
      notifyError('Enter the note title.');
      return;
    }
    closing.current = true;
    setBusy(true);
    dialogRef.close({ title: trimmed, content: content ?? '' });
  };

  return (
    <div className="mat-dialog-wrapper">
      <DialogContent>
        <form noValidate onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>{data?.heading || 'Principles Activites'}</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="d-flex gap-15">
            <div>
              <div className="form-field">
                <p className="fw-bold">
                  Title <RequiredMark />
                </p>
                <InputField
                  fieldClassName="wp-100"
                  required
                  invalid={submitted && (title ?? '').trim().length === 0}
                  maxLength={data?.maxTitleLength ?? 255}
                  name="title"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </div>
              <div className="form-field w-510">
                <p className="fw-bold">Note Contents</p>
                <RichTextEditor id="editor1" value={content} onChange={setContent} config={editorConfig} />
                {hasDefault && (
                  <div className="d-flex justify-content-end pt-5">
                    <a className="link hyperlink" onClick={resetDefault}>
                      Reset Default
                    </a>
                  </div>
                )}
              </div>
            </div>
            {/* ap5-a5: the tags the accounts substitute; clicking one adds it to the wording. */}
            <NoteTagsTab groups={data?.tagGroups ?? CAPIUM_TAG_GROUPS} onInsert={insertTag} />
          </div>
          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={busy} onClick={onSave}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
